/**
 * Reading a vendor's spreadsheet in the browser.
 *
 * The file never leaves the vendor's computer as a file: we read it here and
 * send its rows. A row keeps the number the spreadsheet shows for it, because
 * "row 88 has a price we can't read" is only useful if row 88 is row 88.
 */

export type Cell = string | number | boolean | null;

export interface SheetRow {
  /** The row's number as the spreadsheet shows it (1-based). */
  r: number;
  c: Cell[];
}

export interface ParsedSheet {
  file: string;
  /** The tab's name; empty for a CSV, which has none. */
  sheet: string;
  headers: string[];
  rows: SheetRow[];
}

export const MAX_COLUMNS = 80;

export type SpreadsheetProblem = 'not_a_spreadsheet' | 'old_excel' | 'unreadable' | 'too_many_columns' | 'no_rows';

export class SpreadsheetError extends Error {
  constructor(
    public readonly problem: SpreadsheetProblem,
    public readonly file: string,
  ) {
    super(problem);
    this.name = 'SpreadsheetError';
  }
}

function toCell(value: unknown): Cell {
  if (value == null) return null;
  if (value instanceof Date) {
    // A date cell is a calendar day; the reader hands it over as midnight UTC.
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text === '' ? null : text.slice(0, 5000);
}

const filled = (row: Cell[]) => row.filter((c) => c !== null).length;

/**
 * A grid of cells → a sheet. The header is the first row with at least two
 * filled cells (a title line above the table has one); rows with nothing in
 * them are dropped, the rest keep their spreadsheet row number. Null when the
 * tab holds no table.
 */
export function sheetFromGrid(file: string, sheet: string, grid: unknown[][]): ParsedSheet | null {
  const cells = grid.map((row) => row.map(toCell));
  let headerAt = cells.findIndex((row) => filled(row) >= 2);
  if (headerAt < 0) headerAt = cells.findIndex((row) => filled(row) >= 1);
  if (headerAt < 0) return null;

  const header = cells[headerAt];
  let width = header.length;
  while (width > 0 && header[width - 1] === null) width--;
  if (width > MAX_COLUMNS) throw new SpreadsheetError('too_many_columns', file);

  const headers = header.slice(0, width).map((h) => (h === null ? '' : String(h).slice(0, 200)));
  const rows: SheetRow[] = [];
  for (let i = headerAt + 1; i < cells.length; i++) {
    const c = cells[i].slice(0, width);
    while (c.length < width) c.push(null);
    if (filled(c) > 0) rows.push({ r: i + 1, c });
  }
  return rows.length > 0 ? { file, sheet, headers, rows } : null;
}

/**
 * Comma-, semicolon- or tab-separated text, with quoted fields. Whichever of
 * the three separates the first line into the most fields is the separator —
 * a spreadsheet saved in a decimal-comma locale uses semicolons.
 */
export function parseCsv(text: string): string[][] {
  const body = text.replace(/^\uFEFF/, '');
  const firstLine = body.split(/\r?\n/, 1)[0] ?? '';
  const count = (sep: string) => firstLine.split(sep).length;
  const sep = [',', ';', '\t'].reduce((best, s) => (count(s) > count(best) ? s : best), ',');

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (quoted) {
      if (ch === '"') {
        if (body[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field === '') quoted = true;
    else if (ch === sep) {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && body[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Every tab of a file that holds a table. */
export async function readSpreadsheet(file: File): Promise<ParsedSheet[]> {
  const name = file.name;
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'xls') throw new SpreadsheetError('old_excel', name);
  if (!['xlsx', 'csv', 'tsv', 'txt'].includes(ext)) throw new SpreadsheetError('not_a_spreadsheet', name);

  let sheets: (ParsedSheet | null)[];
  if (ext === 'xlsx') {
    try {
      // Loaded on demand: only someone importing a spreadsheet needs the reader.
      const { default: readXlsxFile } = await import('read-excel-file/browser');
      const tabs = await readXlsxFile(file);
      sheets = tabs.map((tab) => sheetFromGrid(name, tab.sheet, tab.data as unknown[][]));
    } catch (e) {
      if (e instanceof SpreadsheetError) throw e;
      throw new SpreadsheetError('unreadable', name);
    }
  } else {
    sheets = [sheetFromGrid(name, '', parseCsv(await file.text()))];
  }
  const found = sheets.filter((s): s is ParsedSheet => s !== null);
  if (found.length === 0) throw new SpreadsheetError('no_rows', name);
  return found;
}
